import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, List, Button, Typography, Progress, Empty, Tag, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
  FileDoneOutlined, 
  FieldTimeOutlined, 
  TrophyOutlined, 
  FileTextOutlined,
  CodeOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { 
  getStudentDashboardStats, 
  getStudentPendingExercises, 
  getStudentRecentActivities 
} from '../../api/studentDashboard';

const { Title, Text, Paragraph } = Typography;

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    completedExercises: 0,
    pendingExercises: 0,
    totalScore: 0,
    averageScore: 0
  });
  const [exercises, setExercises] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    // 获取真实数据
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 并行请求所有数据
        const [statsData, exercisesData, activitiesData] = await Promise.all([
          getStudentDashboardStats(),
          getStudentPendingExercises(3), // 只获取3条
          getStudentRecentActivities(4)  // 只获取4条
        ]);
        
        // 更新状态
        setStats(statsData);
        setExercises(exercisesData);
        setRecentActivities(activitiesData);
      } catch (error) {
        console.error('获取学生仪表盘数据失败:', error);
        message.error('获取数据失败，请刷新页面重试');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusColor = (status) => {
    const statusMap = {
      'completed': '#52c41a',
      'in_progress': '#1890ff',
      'pending': '#faad14',
      'overdue': '#f5222d'
    };
    return statusMap[status] || '#000000';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'completed': '已完成',
      'in_progress': '进行中',
      'pending': '待开始',
      'overdue': '已逾期'
    };
    return statusMap[status] || '未知状态';
  };

  const getPriorityTag = (priority) => {
    const priorityConfig = {
      'high': { color: '#f5222d', text: '高优先级' },
      'medium': { color: '#faad14', text: '中优先级' },
      'low': { color: '#52c41a', text: '低优先级' }
    };
    const config = priorityConfig[priority] || { color: '#d9d9d9', text: '默认优先级' };
    
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  return (
    <div>
      <Title level={2} style={{ marginBottom: 24 }}>学习仪表盘</Title>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="已完成练习"
              value={stats.completedExercises}
              prefix={<FileDoneOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="待完成练习"
              value={stats.pendingExercises}
              prefix={<FieldTimeOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="总得分"
              value={stats.totalScore}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={loading}>
            <Statistic
              title="平均得分"
              value={stats.averageScore}
              suffix="/ 100"
              precision={1}
              prefix={<CodeOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 待完成练习和最近活动 */}
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card 
            title="待完成练习" 
            loading={loading}
            extra={<Button type="link" onClick={() => navigate('/student/exercises')}>查看全部</Button>}
            style={{ marginBottom: 16 }}
          >
            {exercises.length > 0 ? (
              <List
                dataSource={exercises}
                renderItem={item => (
                  <List.Item
                    actions={[
                      <Button type="primary" onClick={() => navigate(`/student/exercises/${item.id}`)}>
                        {item.progress > 0 ? '继续练习' : '开始练习'}
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: 8 }}>{item.title}</span>
                          {getPriorityTag(item.priority)}
                          <Tag color={getStatusColor(item.status)} style={{ marginLeft: 8 }}>
                            {getStatusText(item.status)}
                          </Tag>
                        </div>
                      }
                      description={
                        <div>
                          <div style={{ marginBottom: 8 }}>
                            <CalendarOutlined /> 截止日期: {item.dueDate}
                          </div>
                          <Progress percent={item.progress} size="small" />
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description={loading ? "加载中..." : "暂无待完成练习"} />
            )}
          </Card>
        </Col>
        
        <Col xs={24} lg={8}>
          <Card title="最近活动" loading={loading}>
            {recentActivities.length > 0 ? (
              <List
                dataSource={recentActivities}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        item.type === 'submission' || item.type === 'completion' ? (
                          <FileDoneOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                        ) : item.type === 'practice' ? (
                          <CodeOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                        ) : (
                          <FileTextOutlined style={{ fontSize: '24px', color: '#faad14' }} />
                        )
                      }
                      title={item.title}
                      description={
                        <div>
                          <div>{item.time}</div>
                          {item.score !== undefined && item.score !== null && (
                            <div>得分: <span style={{ color: '#1890ff' }}>{item.score}</span></div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description={loading ? "加载中..." : "暂无活动记录"} />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StudentDashboard;  
